import { c as _c } from "react/compiler-runtime";
import type { PaginationState } from '@tanstack/react-table';
import { isDefined } from '@morpho-org/morpho-ts';
import { skipToken, useQuery } from '@tanstack/react-query';
import { type Row, useTable } from '@tanstack/react-table';
import { useState } from 'react';
import { ChainConfig } from '@/config';
import useTableSettings from '@/helpers/cookieStorage/userSettings/tableSettings/client';
import { PageKey } from '@/helpers/cookieStorage/userSettings/types';
import { useSkeletonRows } from '@/hooks/useSkeletonRows';
import { useViewedAccountAddress } from '@/hooks/useViewedAccountAddress';
import { orpc } from '@/lib/orpc/client';
import type { DashboardActivityTableFeatures, EarnActivityTableRow } from '../config';
import { DASHBOARD_EARN_TRANSACTIONS_STORAGE_KEY, DASHBOARD_TRANSACTIONS_DEFAULT_TABLE_SETTINGS, dashboardActivityTableFeatures, PAGE_SIZE } from '../config/DashboardActivity.config';
import { columnsDefs } from '../config/DashboardEarnActivity.columns';
import { toEarnActivityRow } from './activityRows';
export const useDashboardEarnActivityTable = () => {
  const $ = _c(37);
  const {
    address
  } = useViewedAccountAddress();
  let t0;
  if ($[0] !== address) {
    t0 = {
      pageKey: PageKey.Dashboard,
      tableSettingsObjectKey: DASHBOARD_EARN_TRANSACTIONS_STORAGE_KEY,
      defaultTableSettings: DASHBOARD_TRANSACTIONS_DEFAULT_TABLE_SETTINGS,
      resetFiltersKey: address
    };
    $[0] = address;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const {
    sorting,
    setSorting,
    columnFilters,
    setColumnFilters
  } = useTableSettings(t0);
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      pageIndex: 0,
      pageSize: PAGE_SIZE
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [pagination, setPagination] = useState(t1);
  let t2;
  if ($[3] !== columnFilters) {
    t2 = columnFilters.find(_temp);
    $[3] = columnFilters;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const globalFilterItem = t2;
  const globalFilter = (globalFilterItem?.value ?? "") as string;
  let t3;
  if ($[5] !== address) {
    t3 = orpc.user.earnTransactions.queryOptions({
      input: address ? {
        userAddress: address,
        chainIds: ChainConfig.AVAILABLE_CHAIN_IDS
      } : skipToken,
      staleTime: 30000
    });
    $[5] = address;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    data,
    isLoading,
    error
  } = useQuery(t3);
  let t4;
  if ($[7] !== data?.items) {
    t4 = data?.items.map(toEarnActivityRow).filter(isDefined) ?? [];
    $[7] = data?.items;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const formattedData = t4;
  const hasNoTransactionsData = formattedData.length === 0;
  let t5;
  if ($[9] !== columnFilters) {
    t5 = columnFilters.filter(_temp2);
    $[9] = columnFilters;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const tableColumnFilters = t5;
  const globalFilterFn = _temp3;
  const t6 = !hasNoTransactionsData;
  let t7;
  if ($[11] !== globalFilter || $[12] !== pagination || $[13] !== sorting || $[14] !== tableColumnFilters) {
    t7 = {
      sorting,
      columnFilters: tableColumnFilters,
      globalFilter,
      pagination
    };
    $[11] = globalFilter;
    $[12] = pagination;
    $[13] = sorting;
    $[14] = tableColumnFilters;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== formattedData || $[17] !== setSorting || $[18] !== t6 || $[19] !== t7) {
    t8 = {
      features: dashboardActivityTableFeatures,
      data: formattedData,
      columns: columnsDefs,
      onSortingChange: setSorting,
      onPaginationChange: setPagination,
      enableSortingRemoval: false,
      sortDescFirst: true,
      enableSorting: t6,
      globalFilterFn,
      state: t7
    };
    $[16] = formattedData;
    $[17] = setSorting;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const transactionsTable = useTable(t8);
  let t9;
  if ($[21] !== transactionsTable) {
    t9 = transactionsTable.getRowModel();
    $[21] = transactionsTable;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const hasNoTransactions = t9.rows.length === 0;
  let t10;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      first: PAGE_SIZE,
      totalCount: PAGE_SIZE,
      pagination: {
        pageIndex: 0,
        pageSize: PAGE_SIZE
      },
      columnsDefs
    };
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const loadingRows = useSkeletonRows(t10);
  const t11 = !isLoading && !error && formattedData.length === 0;
  const t12 = data?.partial ?? false;
  let t13;
  if ($[24] !== columnFilters || $[25] !== error || $[26] !== formattedData || $[27] !== hasNoTransactions || $[28] !== hasNoTransactionsData || $[29] !== isLoading || $[30] !== loadingRows || $[31] !== pagination || $[32] !== setColumnFilters || $[33] !== t11 || $[34] !== t12 || $[35] !== transactionsTable) {
    t13 = {
      isNotFound: t11,
      loading: isLoading,
      partial: t12,
      error,
      items: formattedData,
      transactionsTable,
      hasNoTransactions,
      hasNoTransactionsData,
      columnFilters,
      setColumnFilters,
      pagination,
      loadingRows
    };
    $[24] = columnFilters;
    $[25] = error;
    $[26] = formattedData;
    $[27] = hasNoTransactions;
    $[28] = hasNoTransactionsData;
    $[29] = isLoading;
    $[30] = loadingRows;
    $[31] = pagination;
    $[32] = setColumnFilters;
    $[33] = t11;
    $[34] = t12;
    $[35] = transactionsTable;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  return t13;
};
function _temp(filter) {
  return filter.id === "global";
}
function _temp2(filter_0) {
  return filter_0.id !== "global";
}
function _temp3(row, _columnId, filterValue) {
  if (!filterValue) {
    return true;
  }
  const searchValue = filterValue.toLowerCase();
  const rowData = row.original;
  if (rowData.transactionHash.toLowerCase().includes(searchValue)) {
    return true;
  }
  if (rowData.vaultName?.toLowerCase().includes(searchValue)) {
    return true;
  }
  return false;
}